/**
 * Content-area loading placeholder shown while a route's lazy view chunk loads
 * (the `loading` fallback of `clientPage`). Replaces the full-screen spinner:
 * the persistent layout (header/nav) stays in place and only the content region
 * shows a structural skeleton, so switching sections feels instant instead of
 * flashing a blank spinner.
 *
 * Pure HTML/CSS (no Chakra) so it is cheap and theme-correct via the `html.dark`
 * class that next-themes sets before paint. Mirrors the visual language of the
 * initial-load AppSkeleton, minus the chrome the layout already renders.
 */
export const ViewSkeleton = () => (
  <div className="cmn-vskel" aria-hidden="true" aria-busy="true">
    <style>{VIEW_SKELETON_CSS}</style>
    <div className="cmn-vskel__title cmn-vskel__shimmer" />
    <div className="cmn-vskel__subtitle cmn-vskel__shimmer" />
    <div className="cmn-vskel__cards">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="cmn-vskel__card cmn-vskel__shimmer" />
      ))}
    </div>
  </div>
)

const VIEW_SKELETON_CSS = `
.cmn-vskel { max-width: 1280px; margin: 0 auto; padding: 24px 16px 48px; }
.cmn-vskel__title { width: 240px; max-width: 60%; height: 32px; border-radius: 8px; margin-bottom: 16px; }
.cmn-vskel__subtitle { width: 360px; max-width: 80%; height: 18px; border-radius: 6px; margin-bottom: 32px; }
.cmn-vskel__cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; }
.cmn-vskel__card { height: 200px; border-radius: 12px; }

.cmn-vskel__shimmer { background: #E2E8F0; position: relative; overflow: hidden; }
html.dark .cmn-vskel__shimmer { background: #4B5563; }
.cmn-vskel__shimmer::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent);
  animation: cmn-vskel-sweep 1.4s ease-in-out infinite;
}
html.dark .cmn-vskel__shimmer::after {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
}
@keyframes cmn-vskel-sweep { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .cmn-vskel__shimmer::after { animation: none; } }
`
